import { useCallback, useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import debounce from 'lodash.debounce';

import TaskContext from '@atom/components/workOrderPreview/taskView/TaskContext';
import WorkOrderPreviewContext from '@atom/components/workOrderPreview/WorkOrderPreviewContext';
import { TASK_UPDATE } from '@atom/graph/task';
import { DatePicker } from '@atom/mui';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { convertDateToMillisGMTMidday } from '@atom/utilities/timeUtilities';

import classes from './taskView.module.css';

const DEBOUNCE_TIME = 1000;

const TaskStartTime = () => {
  const { workOrderDetail, refetch } = useContext(WorkOrderPreviewContext);
  const { task } = useContext(TaskContext);
  const [startTime, setStartTime] = useState<Date>(
    task.startTime ? new Date(task.startTime) : null,
  );

  const [taskUpdate] = useMutation(TASK_UPDATE);

  useEffect(() => {
    setStartTime(task.startTime ? new Date(task.startTime) : null);
  }, [task.startTime]);

  const handleSubmit = async (date: Date) => {
    await taskUpdate({
      variables: {
        input: {
          id: task.id,
          workOrderId: workOrderDetail.id,
          startTime: convertDateToMillisGMTMidday(date),
        },
      },
    });

    refetch();
  };

  const debouncedHandleSubmit = useCallback(
    debounce(handleSubmit, DEBOUNCE_TIME),
    [task.id, workOrderDetail.id],
  );

  const onChange = (date?: Date) => {
    setStartTime(date);

    if (!date) {
      return;
    } else if (!Number.isNaN(date.valueOf())) {
      debouncedHandleSubmit(date);
    }
  };

  const isDisabled =
    workOrderDetail.isClosed || doesNotHaveRolePermissions(ROLE_SETS.MANAGER);

  return (
    <div className={classes.timeRowItem}>
      <div className={classes.label}>Start Date</div>
      <div className={classes.timePickerContainer}>
        <DatePicker
          value={startTime}
          onChange={onChange}
          disabled={isDisabled}
        />
      </div>
    </div>
  );
};

export default TaskStartTime;
